class CustomVideo extends HTMLElement {
  constructor() {
    super();
    this._isInitialized = false;
    this._initTimeoutId = null;
    this._cleanup = [];
  }

  connectedCallback() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    // Wait for Light DOM children to be parsed
    this._initTimeoutId = window.setTimeout(() => {
      const wrapper = this;
      const video = wrapper.querySelector('video');
      const playBtn = wrapper.querySelector('.play-pause');
      const centralPlayBtn = wrapper.querySelector('[data-hero-card-play-pause]');
      const progress = wrapper.querySelector('.progress');
      const muteBtn = wrapper.querySelector('.mute');
      const fullscreenBtn = wrapper.querySelector('.fullscreen');
      const timeDisplay = wrapper.querySelector('.time-display');
      const timeDisplayText = wrapper.querySelector('.time-display-text');

      if (!video) return;

      // Programmatic delayed autoplay
      const shouldAutoplay = wrapper.getAttribute('data-autoplay') === 'true';
      const delayAttr = wrapper.getAttribute('data-autoplay-delay');

      if ((shouldAutoplay || video.hasAttribute('autoplay')) && !video.hasAttribute('data-deferred-video')) {
        if (delayAttr) {
          const delayTime = parseInt(delayAttr, 10);
          setTimeout(() => {
            video.play?.().catch(() => { });
          }, delayTime);
        } else {
          video.play?.().catch(() => { });
        }
      }

      // Prevent video from taking focus
      video.setAttribute('tabindex', '-1');

      let liveRegion = wrapper.querySelector('.sr-only');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.className = 'sr-only';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        wrapper.appendChild(liveRegion);
      }

      function formatTime(time) {
        if (isNaN(time)) return '0:00';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
      }

      function formatSpokenTime(time) {
        if (isNaN(time)) return '0 seconds';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60);
        if (m > 0) {
          return `${m} minute${m !== 1 ? 's' : ''} ${s} second${s !== 1 ? 's' : ''}`;
        }
        return `${s} second${s !== 1 ? 's' : ''}`;
      }

      let duration = video.duration && !isNaN(video.duration) ? video.duration : 0;

      const onLoadedMetadata = () => {
        duration = video.duration;
        if (progress) progress.setAttribute('aria-valuemax', Math.floor(duration));
        if (timeDisplay && timeDisplayText) {
          timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
          timeDisplay.setAttribute('aria-label', `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`);
        }
      };
      video.addEventListener('loadedmetadata', onLoadedMetadata);
      this._cleanup.push(() => video.removeEventListener('loadedmetadata', onLoadedMetadata));

      if (duration > 0 && timeDisplay && timeDisplayText) {
        timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
        timeDisplay.setAttribute('aria-label', `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`);
      }

      const togglePlay = () => {
        video.paused ? video.play() : video.pause();
      };

      if (playBtn) {
        const onPlayBtnClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        };
        playBtn.addEventListener('click', onPlayBtnClick);
        this._cleanup.push(() => playBtn.removeEventListener('click', onPlayBtnClick));
      }

      if (centralPlayBtn) {
        const onCentralPlayClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        };
        centralPlayBtn.addEventListener('click', onCentralPlayClick);
        this._cleanup.push(() => centralPlayBtn.removeEventListener('click', onCentralPlayClick));
      }

      const syncPlayStateUI = (paused) => {
        wrapper.classList.toggle('is-paused', paused);

        if (playBtn) {
          playBtn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
          playBtn.setAttribute('data-video-play', paused ? 'true' : 'false');
          const playIcon = playBtn.querySelector('[data-hero-card-icon="play"]');
          const pauseIcon = playBtn.querySelector('[data-hero-card-icon="pause"]');
          if (playIcon) playIcon.hidden = !paused;
          if (pauseIcon) pauseIcon.hidden = paused;
        }

        if (centralPlayBtn) {
          const playIcon = centralPlayBtn.querySelector('[data-hero-card-icon="play"]');
          const pauseIcon = centralPlayBtn.querySelector('[data-hero-card-icon="pause"]');
          const initialLabel = centralPlayBtn.getAttribute('aria-label') || 'Play video';
          const labelDesc = initialLabel.replace(/^(Play|Pause)\s+/i, '');

          centralPlayBtn.setAttribute('aria-label', (paused ? 'Play ' : 'Pause ') + (labelDesc || 'video'));
          centralPlayBtn.setAttribute('data-video-play', paused ? 'true' : 'false');

          if (playIcon) playIcon.hidden = !paused;
          if (pauseIcon) pauseIcon.hidden = paused;
        }
      };

      // Keyboard fix
      wrapper.querySelectorAll('button').forEach(btn => {
        const onButtonKeydown = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            btn.click();
          }
        };
        btn.addEventListener('keydown', onButtonKeydown);
        this._cleanup.push(() => btn.removeEventListener('keydown', onButtonKeydown));
      });

      // Visual and ARIA update
      const onTimeUpdate = () => {
        duration = video.duration && !isNaN(video.duration) ? video.duration : duration;

        if (!isNaN(duration) && duration > 0) {
          const percent = (video.currentTime / duration) * 100;

          if (progress) {
            progress.style.setProperty('--progress', percent + '%');
            // Keep ARIA in sync
            progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
            progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));
          }

          if (timeDisplay && timeDisplayText) {
            timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
            timeDisplay.setAttribute(
              'aria-label',
              `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`
            );
          }
        }
      };
      video.addEventListener('timeupdate', onTimeUpdate);
      this._cleanup.push(() => video.removeEventListener('timeupdate', onTimeUpdate));

      if (progress) {
        const onProgressFocusin = () => {
          progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));
          progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
        };
        progress.addEventListener('focusin', onProgressFocusin);
        this._cleanup.push(() => progress.removeEventListener('focusin', onProgressFocusin));

        const onProgressKeydown = (e) => {
          const step = 5;
          if (!duration || isNaN(duration)) return;

          if (['ArrowRight', 'ArrowLeft'].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();

            if (e.key === 'ArrowRight') {
              video.currentTime = Math.min(video.currentTime + step, duration);
            }

            if (e.key === 'ArrowLeft') {
              video.currentTime = Math.max(video.currentTime - step, 0);
            }

            progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
            progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));

            const currentSpoken = formatSpokenTime(video.currentTime);
            const totalSpoken = formatSpokenTime(duration);

            liveRegion.textContent = `Time ${currentSpoken} of ${totalSpoken}`;
          }
        };
        progress.addEventListener('keydown', onProgressKeydown);
        this._cleanup.push(() => progress.removeEventListener('keydown', onProgressKeydown));

        const onProgressClick = (e) => {
          if (!duration || isNaN(duration)) return;
          const rect = progress.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const width = rect.width;
          if (!width) return;

          const percent = clickX / width;
          const newTime = percent * duration;

          video.currentTime = newTime;

          progress.style.setProperty('--progress', (percent * 100) + '%');
          progress.setAttribute('aria-valuenow', Math.floor(newTime));
          progress.setAttribute('aria-valuetext', formatSpokenTime(newTime));

          const currentSpoken = formatSpokenTime(newTime);
          const totalSpoken = formatSpokenTime(duration);
          liveRegion.textContent = `Time ${currentSpoken} of ${totalSpoken}`;

          progress.focus();
        };
        progress.addEventListener('click', onProgressClick);
        this._cleanup.push(() => progress.removeEventListener('click', onProgressClick));
      }

      if (muteBtn) {
        const onMuteClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          video.muted = !video.muted;
          muteBtn.setAttribute('data-mute', video.muted);
          muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video')
        };
        muteBtn.addEventListener('click', onMuteClick);
        this._cleanup.push(() => muteBtn.removeEventListener('click', onMuteClick));
      }

      if (fullscreenBtn) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        let iosFakeFullscreen = false;

        const updateFullscreenUI = (inFullscreen) => {
          fullscreenBtn.setAttribute('aria-label', inFullscreen ? 'Exit fullscreen' : 'Fullscreen');
          fullscreenBtn.setAttribute('data-video-maximize', inFullscreen ? 'false' : 'true');
        };

        const enterFullscreen = () => {
          if (isIOS) {
            if (video.webkitEnterFullscreen) {
              video.webkitEnterFullscreen();
            } else {
              iosFakeFullscreen = true;
              wrapper.classList.add('ios-fake-fullscreen');
            }
            updateFullscreenUI(true);
            requestAnimationFrame(() => fullscreenBtn.focus());
          } else {
            (wrapper.requestFullscreen || wrapper.webkitRequestFullscreen)?.call(wrapper);
          }
        };

        const exitFullscreen = () => {
          if (isIOS) {
            iosFakeFullscreen = false;
            wrapper.classList.remove('ios-fake-fullscreen');
            updateFullscreenUI(false);
            requestAnimationFrame(() => fullscreenBtn.focus());
          } else {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
          }
        };

        const isInFullscreen = () =>
          isIOS ? iosFakeFullscreen : !!(document.fullscreenElement || document.webkitFullscreenElement);

        const onFullscreenClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          isInFullscreen() ? exitFullscreen() : enterFullscreen();
        };
        fullscreenBtn.addEventListener('click', onFullscreenClick);
        this._cleanup.push(() => fullscreenBtn.removeEventListener('click', onFullscreenClick));

        if (!isIOS) {
          const onFullscreenChange = () => {
            updateFullscreenUI(isInFullscreen());
            requestAnimationFrame(() => fullscreenBtn.focus());
          };
          document.addEventListener('fullscreenchange', onFullscreenChange);
          document.addEventListener('webkitfullscreenchange', onFullscreenChange);
          this._cleanup.push(() => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
          });
        }
      }

      // AUTO PAUSE LOGIC
      const autoPauseAttr = wrapper.getAttribute('data-auto-pause');
      const defaultPauseSeconds = 5;
      let pauseAfterSecondsAttr = wrapper.getAttribute('data-pause-after-seconds');

      if (!pauseAfterSecondsAttr) {
        const heroCard = wrapper.closest('hero-media-card');
        if (heroCard) {
          pauseAfterSecondsAttr = heroCard.getAttribute('data-pause-after-seconds');
        }
      }

      const parsedPauseAfterSeconds = pauseAfterSecondsAttr
        ? parseInt(pauseAfterSecondsAttr, 10)
        : defaultPauseSeconds;

      const pauseAfterSeconds = Number.isFinite(parsedPauseAfterSeconds) && parsedPauseAfterSeconds > 0
        ? parsedPauseAfterSeconds
        : defaultPauseSeconds;

      let autoPauseListener = null;
      let hasAutoPausedOnce = false;

      const clearAutoPause = () => {
        if (autoPauseListener) {
          video.removeEventListener('timeupdate', autoPauseListener);
          autoPauseListener = null;
        }
      };

      const scheduleAutoPause = () => {
        if (autoPauseAttr === 'false' || pauseAfterSeconds <= 0 || hasAutoPausedOnce) return;
        clearAutoPause();
        autoPauseListener = () => {
          if (video.currentTime >= pauseAfterSeconds) {
            hasAutoPausedOnce = true;
            clearAutoPause();
            video.pause();
          }
        };
        video.addEventListener('timeupdate', autoPauseListener);
      };


      const onVideoPlay = () => {
        syncPlayStateUI(false);
        scheduleAutoPause();
      };
      video.addEventListener('play', onVideoPlay);
      video.addEventListener('playing', onVideoPlay);
      this._cleanup.push(() => {
        video.removeEventListener('play', onVideoPlay);
        video.removeEventListener('playing', onVideoPlay);
      });

      const onVideoPause = () => {
        syncPlayStateUI(true);
        clearAutoPause();
      };
      video.addEventListener('pause', onVideoPause);
      video.addEventListener('ended', onVideoPause);
      this._cleanup.push(() => {
        video.removeEventListener('pause', onVideoPause);
        video.removeEventListener('ended', onVideoPause);
      });

      if (!video.paused) {
        scheduleAutoPause();
        syncPlayStateUI(false);
      } else {
        syncPlayStateUI(true);
      }

      const onVideoClick = () => togglePlay();
      video.addEventListener('click', onVideoClick);
      this._cleanup.push(() => video.removeEventListener('click', onVideoClick));
    }, 0);
  }

  disconnectedCallback() {
    if (this._initTimeoutId) {
      window.clearTimeout(this._initTimeoutId);
      this._initTimeoutId = null;
    }

    for (const cleanupFn of this._cleanup) {
      cleanupFn();
    }

    this._cleanup = [];
    this._isInitialized = false;
  }
}

if (!customElements.get('custom-video')) {
  customElements.define('custom-video', CustomVideo);
}